import { useCallback, useEffect, useState } from 'react';
import pluralize from 'pluralize';
import sortBy from 'lodash/sortBy';
import { useSnackbar } from 'notistack';
import { Button, ButtonBase, CircularProgress, Stack, Typography, styled } from '@mui/material';

import { type ConnectStatementsInstitution } from '../hooks/useConnectStatementsData';
import useConnectedInstitutionResolver from '../hooks/useConnectedInstitutionResolver';
import useUnconfirmedConnections from '../hooks/useUnconfirmedConnections';

import BankListCard from './BankListCard';
import SyncInstitutionRow, { type SyncInstitutionStatus } from './SyncInstitutionRow';
import SyncProgressDialog from './SyncProgressDialog';

import useQuilttConnect from 'modules/common/components/StatementUpload/hooks/useQuilttConnect';
import { getInstitutionSearchForConnector } from 'services/quiltt/utils/quilttResolvability';

// The list overlaps the banner by this much, so the banner reads as a tab behind the card (Figma).
const BANNER_OVERLAP = 2;

const SyncBanner = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(2, 3, 2 + BANNER_OVERLAP),
    marginBottom: theme.spacing(-BANNER_OVERLAP),
    backgroundColor: theme.palette.purple[100],
    borderRadius: theme.spacing(2, 2, 0, 0),
}));

// The redesign's pill button (Figma MasterButton, corner-full). The theme's Button has no pill shape yet.
const SyncButton = styled(Button)({
    borderRadius: 999,
});

const UploadManuallyRow = styled(ButtonBase)(({ theme }) => ({
    ...theme.typography.body1,
    width: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing(2, 3),
    color: theme.palette.text.primary,
}));

interface SyncStatementsCardProps {
    readonly institutions: ConnectStatementsInstitution[];
    /** true/false once known, null while checking. */
    readonly getResolvable: (institutionId: string) => boolean | null;
    /** The check timed out — listed as unknown rather than dropped as unsupported. */
    readonly isTimedOut: (institutionId: string) => boolean;
    readonly enabledInstitutionIds: Set<string>;
    readonly connectorId: string | undefined;
    readonly onEnabled: (institutionId: string, connectionId?: string) => void;
    readonly onUploadManually: () => void;
    /** Where the connector is opened from, for its analytics. Defaults to accounting setup. */
    readonly sourceSurface?: string;
}

/**
 * The connected banks Auto Statements can serve, with one Sync statements action. The rest go to
 * manual upload, so they are not listed.
 *
 * Opening the connector pre-fills its search with the first bank still waiting to sync, so the member
 * never has to rediscover it. That bank is locked for as long as the connector is open — a support
 * check landing late must not swap the prefill, and with it the bank a connection is credited to,
 * mid-session. The connection is then credited to whichever bank the backend says it belongs to,
 * which is not necessarily the prefilled one.
 *
 * A prefill that matches exactly makes the connector skip its search screen, and if that match cannot
 * serve statements the member is stuck on "Institution Not Supported", where Back only returns to the
 * start. So once the member backs out of a prefilled bank, it opens on the plain search from then on.
 *
 * A connection the backend has not confirmed yet is not credited to any bank. The bank it was opened for
 * shows as confirming while the list is re-read in the background, and is credited once the connection
 * lists. It stays syncable, offered after the others, since the member may have connected another bank.
 *
 * After a bank syncs while others still wait, a dialog shows what is done and what is left, and its
 * Continue opens the connector on the next bank. Once the last one syncs and no check is still running,
 * a snackbar says so instead.
 */
export default function SyncStatementsCard({
    institutions,
    getResolvable,
    isTimedOut,
    enabledInstitutionIds,
    connectorId,
    onEnabled,
    onUploadManually,
    sourceSurface = 'onboarding_biz_account_setup',
}: Readonly<SyncStatementsCardProps>) {
    const { enqueueSnackbar } = useSnackbar();
    const [lockedTargetId, setLockedTargetId] = useState<string | null>(null);
    const [searchOnlyIds, setSearchOnlyIds] = useState<ReadonlySet<string>>(() => new Set());
    const [justSyncedId, setJustSyncedId] = useState<string | null>(null);
    const [isCrediting, setIsCrediting] = useState(false);

    const resolveConnectedInstitution = useConnectedInstitutionResolver(institutions);
    const creditConnection = useCallback(
        (institutionId: string, connectionId: string) => {
            onEnabled(institutionId, connectionId);
            setJustSyncedId(institutionId);
        },
        [onEnabled]
    );
    const { confirmingIds, addUnconfirmed } = useUnconfirmedConnections(institutions, creditConnection);

    // A confirming bank stays syncable, since the member may have connected another, but is offered last.
    const pending = sortBy(
        institutions.filter(
            (inst) => getResolvable(inst.institutionId) === true && !enabledInstitutionIds.has(inst.institutionId)
        ),
        (inst) => confirmingIds.has(inst.institutionId)
    );
    const target = (lockedTargetId && institutions.find((inst) => inst.institutionId === lockedTargetId)) || pending[0];

    const releaseTarget = useCallback(() => setLockedTargetId(null), []);

    const handleExitWithoutConnecting = useCallback(() => {
        if (lockedTargetId) {
            setSearchOnlyIds((prev) => new Set(prev).add(lockedTargetId));
        }
        releaseTarget();
    }, [lockedTargetId, releaseTarget]);

    // useQuilttConnect only reports a success that carries a connection.
    const handleSuccess = useCallback(
        async (metadata: { connectionId?: string } | undefined) => {
            releaseTarget();
            if (!target || !metadata?.connectionId) {
                return;
            }
            setIsCrediting(true);
            const institutionId = await resolveConnectedInstitution(metadata.connectionId, target.institutionId);
            setIsCrediting(false);
            if (institutionId) {
                creditConnection(institutionId, metadata.connectionId);
            } else if (institutionId === undefined) {
                addUnconfirmed(metadata.connectionId, target.institutionId);
                enqueueSnackbar(
                    'We’re still confirming your bank connection. It will show as synced here once it’s ready.',
                    { variant: 'info' }
                );
            }
        },
        [target, creditConnection, addUnconfirmed, resolveConnectedInstitution, releaseTarget, enqueueSnackbar]
    );

    const { open: openConnector, isConnecting } = useQuilttConnect({
        connectorId: connectorId ?? '',
        institutionName: target?.institutionName ?? '',
        institutionSearch:
            target && !searchOnlyIds.has(target.institutionId)
                ? getInstitutionSearchForConnector(target.institutionName)
                : undefined,
        sourceSurface,
        onSuccess: handleSuccess,
        onAbort: handleExitWithoutConnecting,
        onError: handleExitWithoutConnecting,
    });

    // However the connector closes — including a success with no connection, which reports nothing —
    // the bank it was opened for stops being locked as the target.
    useEffect(() => {
        if (!isConnecting) {
            releaseTarget();
        }
    }, [isConnecting, releaseTarget]);

    const handleSync = useCallback(() => {
        setJustSyncedId(null);
        if (!target) {
            return;
        }
        setLockedTargetId(target.institutionId);
        openConnector();
    }, [target, openConnector]);

    const closeProgress = useCallback(() => setJustSyncedId(null), []);

    const getStatus = (institution: ConnectStatementsInstitution): SyncInstitutionStatus => {
        if (enabledInstitutionIds.has(institution.institutionId)) {
            return 'enabled';
        }
        if (confirmingIds.has(institution.institutionId)) {
            return 'confirming';
        }
        return isTimedOut(institution.institutionId) ? 'unchecked' : 'not_set_up';
    };

    const canSyncMore = Boolean(target && connectorId);
    const syncedCount = enabledInstitutionIds.size;
    // A check still running may yet add a bank to sync.
    const isChecking = institutions.some(
        (inst) =>
            !enabledInstitutionIds.has(inst.institutionId) &&
            getResolvable(inst.institutionId) === null &&
            !isTimedOut(inst.institutionId)
    );
    useEffect(() => {
        if (justSyncedId && !canSyncMore && !isChecking) {
            enqueueSnackbar('Auto Statements are all set', { variant: 'success' });
            setJustSyncedId(null);
        }
    }, [justSyncedId, canSyncMore, isChecking, enqueueSnackbar]);

    const autoInstitutions = institutions.filter(
        (inst) => enabledInstitutionIds.has(inst.institutionId) || getResolvable(inst.institutionId) === true
    );
    // Enabled wins over a flapping check; a bank still being checked, or whose check timed out, stays
    // until it is known unsupported.
    const listedInstitutions = institutions.filter(
        (inst) =>
            enabledInstitutionIds.has(inst.institutionId) ||
            getResolvable(inst.institutionId) !== false ||
            isTimedOut(inst.institutionId)
    );
    const hasUncheckedBanks = listedInstitutions.some((inst) => getStatus(inst) === 'unchecked');

    return (
        <div>
            {canSyncMore && (
                <SyncBanner direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
                    <Typography variant="bodySemiBold">
                        {pluralize('bank', pending.length, true)} {pluralize('is', pending.length)} ready to sync
                    </Typography>
                    <SyncButton
                        variant="contained"
                        color="onyx"
                        size="small"
                        onClick={handleSync}
                        disabled={isConnecting || isCrediting}
                        sx={{ flexShrink: 0 }}>
                        {isConnecting || isCrediting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Sync statements'
                        )}
                    </SyncButton>
                </SyncBanner>
            )}
            {!canSyncMore && hasUncheckedBanks && (
                <SyncBanner spacing={0.5}>
                    <Typography variant="bodySemiBold">We couldn’t check your banks right now</Typography>
                    <Typography variant="body2">
                        You can upload statements manually, or come back later to sync them automatically.
                    </Typography>
                </SyncBanner>
            )}
            <BankListCard>
                {listedInstitutions.map((institution) => (
                    <SyncInstitutionRow
                        key={institution.institutionId}
                        institution={institution}
                        status={getStatus(institution)}
                    />
                ))}
                {syncedCount === 0 && (
                    <UploadManuallyRow onClick={onUploadManually}>Upload statements manually</UploadManuallyRow>
                )}
            </BankListCard>
            <SyncProgressDialog
                open={Boolean(justSyncedId) && canSyncMore}
                syncedInstitution={institutions.find((inst) => inst.institutionId === justSyncedId)}
                institutions={autoInstitutions}
                enabledInstitutionIds={enabledInstitutionIds}
                onContinue={handleSync}
                onClose={closeProgress}
            />
        </div>
    );
}
